import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import {
    getOrganizations,
    getOrganization,
    createOrganization,
    updateOrganization,
    updateOrganizationStatus,
    CreateOrganizationRequest,
    UpdateOrganizationStatusRequest
} from '../services/organization-service';

export interface OrganizationFilter {
    page?: number;
    pageSize?: number;
    name?: string;
    email?: string;
    code?: string;
    isActive?: boolean;
    /** Every organization in the tenant rather than only the caller's own. */
    all?: boolean;
    enabled?: boolean;
}

/**
 * Custom hook to fetch organizations with pagination and filtering.
 *
 * @param {OrganizationFilter} params - The filter parameters for fetching organizations.
 * @returns {Object} The query result for getting organizations.
 *
 * @example
 * const { data, error, isLoading } = useOrganizations({ page: 0, pageSize: 10 });
 */
export function useOrganizations(params: OrganizationFilter) {
    const { page = 0, pageSize = 10, name, email, code, isActive, all, enabled = true } = params;
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['organizations', params],
        queryFn: () => getOrganizations({
            page,
            pageSize,
            filters: { name, email, code, isActive },
            all
        }, configurationSettings),
        enabled
    });
}

/**
 * Custom hook to fetch a single organization by ID.
 *
 * @param {string} id - The ID of the organization to fetch.
 * @returns {Object} The query result for getting the organization.
 *
 * @example
 * const { data, error, isLoading } = useOrganization(organizationId);
 */
export function useOrganization(id: string) {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['organization', id],
        queryFn: () => getOrganization(id, configurationSettings),
        enabled: !!id
    });
}

export function useGetOrganizationQuery(id: string) {
    const { configurationSettings } = useConfig();

    return useGlobalQuery({
        queryKey: ['getOrganization', id],
        queryFn: () => getOrganization(id, configurationSettings),
        enabled: !!id
    });
}

/**
 * Custom hook to create a new organization.
 * It uses a global mutation to create a new organization and handle success or error states.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the organization creation is successful.
 *
 * @returns {Object} The mutation result for creating the organization.
 *
 * @example
 * const { mutate } = useCreateOrganization({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useCreateOrganization = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['createOrganization'],
        mutationFn: (data: CreateOrganizationRequest) => createOrganization(data, configurationSettings),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('ORGANIZATION_CREATE_SUCCESS'),
            });

            options?.onSuccess?.();
        },
        onError: (error: any) => {
            const errorData = error?.error as any;
            if (errorData?.errors?.Name === 'Name already exists') {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: t('ORGANIZATION_ALREADY_EXISTS'),
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: t('SOMETHING_WENT_WRONG'),
                    description: error?.error?.message ?? t('ORGANIZATION_CREATE_FAILED'),
                });
            }
        },
    });
};

/**
 * Custom hook to update an existing organization.
 * It uses a global mutation to update the organization and handle success or error states.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the organization update is successful.
 *
 * @returns {Object} The mutation result for updating the organization.
 *
 * @example
 * const { mutate } = useUpdateOrganization({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useUpdateOrganization = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['updateOrganization'],
        mutationFn: ({ id, data }: { id: string; data: Partial<CreateOrganizationRequest> }) =>
            updateOrganization(id, data, configurationSettings),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            queryClient.invalidateQueries({ queryKey: ['organization'] });
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('ORGANIZATION_UPDATE_SUCCESS'),
            });

            options?.onSuccess?.();
        },
        onError: (error) => {
            toast({
                variant: 'destructive',
                title: t('SOMETHING_WENT_WRONG'),
                description: error?.error?.message ?? t('ORGANIZATION_UPDATE_FAILED'),
            });
        },
    });
};

/**
 * Custom hook to update organization status (activate/deactivate).
 * It uses a global mutation to update the organization status and handle success or error states.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the organization status update is successful.
 *
 * @returns {Object} The mutation result for updating the organization status.
 *
 * @example
 * const { mutate } = useUpdateOrganizationStatus({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useUpdateOrganizationStatus = (options?: { onSuccess?: () => void }) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['updateOrganizationStatus'],
        mutationFn: (data: UpdateOrganizationStatusRequest) =>
            updateOrganizationStatus(data, configurationSettings),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            queryClient.invalidateQueries({ queryKey: ['organization'] });

            const statusMessage = variables.isActive
                ? t('ORGANIZATION_ACTIVATED_SUCCESS')
                : t('ORGANIZATION_STATUS_UPDATE_SUCCESS');

            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: statusMessage,
            });

            options?.onSuccess?.();
        },
        onError: (error) => {
            toast({
                variant: 'destructive',
                title: t('SOMETHING_WENT_WRONG'),
                description: error?.error?.message ?? t('ORGANIZATION_STATUS_UPDATE_FAILED'),
            });
        },
    });
}; 
